import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { getAuthToken } from "../../utils/session";
import {
  ArrowBackRounded,
  SearchRounded,
  ShieldRounded,
  WorkspacePremiumRounded,
  StarRounded,
  PersonOutlineRounded,
  AdminPanelSettingsRounded,
} from "@mui/icons-material";

export const ADMIN_COLORS = {
  page: "#F6F8FB",
  surface: "#FFFFFF",
  surfaceSoft: "#F9FBFD",
  border: "rgba(15,36,56,0.10)",
  borderStrong: "rgba(15,36,56,0.18)",
  text: "#172033",
  textStrong: "#102840",
  muted: "#64748B",
  mutedSoft: "#94A3B8",
  primary: "#1E4B81",
  primaryDark: "#102840",
  primarySoft: "#EEF5FF",
  accent: "#D37D36",
  accentSoft: "#FFF4EA",
  success: "#14A67A",
  successSoft: "#EAFBF6",
  danger: "#D32F2F",
  dangerSoft: "#FFEBEE",
  warning: "#C47A1F",
  warningSoft: "#FFF7E8",
  shadow: "0 18px 48px rgba(15,36,56,0.07)",
};

export const FONT_FAMILY = 'Inter, Manrope, "SF Pro Display", "Segoe UI", Roboto, Arial, sans-serif';

export const ROLE_OPTIONS = [
  { value: "ROLE_FREEMIUM", label: "Freemium", color: ADMIN_COLORS.primary, bg: ADMIN_COLORS.primarySoft, icon: <PersonOutlineRounded /> },
  { value: "ROLE_PREMIUM", label: "Premium", color: "#667085", bg: "#F3F4F6", icon: <StarRounded /> },
  { value: "ROLE_PREMIUMPRO", label: "Premium Pro", color: ADMIN_COLORS.accent, bg: ADMIN_COLORS.accentSoft, icon: <WorkspacePremiumRounded /> },
  { value: "ROLE_ADMIN", label: "Admin", color: ADMIN_COLORS.danger, bg: ADMIN_COLORS.dangerSoft, icon: <AdminPanelSettingsRounded /> },
];

export const getRoleMeta = (role) => ROLE_OPTIONS.find((item) => item.value === role) || ROLE_OPTIONS[0];

export const PROCESS_META = {
  Gobierno: { label: "Gobierno", color: ADMIN_COLORS.primary, bg: ADMIN_COLORS.primarySoft },
  Gestion: { label: "Gestión", color: ADMIN_COLORS.accent, bg: ADMIN_COLORS.accentSoft },
  Gestión: { label: "Gestión", color: ADMIN_COLORS.accent, bg: ADMIN_COLORS.accentSoft },
  Calidad: { label: "Calidad", color: ADMIN_COLORS.success, bg: ADMIN_COLORS.successSoft },
};

export const getProcessMeta = (type) => PROCESS_META[type] || { label: type || "Sin tipo", color: ADMIN_COLORS.muted, bg: "#F3F4F6" };

export const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const useAdminGuard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    try {
      const decoded = jwtDecode(token);
      if (decoded?.role !== "ROLE_ADMIN") {
        navigate("/autoevaluaciones", { replace: true });
      }
    } catch (error) {
      Cookies.remove("token");
      navigate("/login", { replace: true });
    }
  }, [navigate]);
};

export const adminPageSx = {
  minHeight: "100vh",
  bgcolor: ADMIN_COLORS.page,
  background:
    "radial-gradient(circle at 15% 0%, rgba(30,75,129,.08), transparent 30%), radial-gradient(circle at 90% 8%, rgba(211,125,54,.07), transparent 28%), linear-gradient(180deg,#F6F8FB 0%,#FFFFFF 45%,#F6F8FB 100%)",
  px: { xs: 1.3, md: 2.2 },
  py: { xs: 2, md: 2.6 },
  fontFamily: FONT_FAMILY,
};

export const cardSx = {
  bgcolor: ADMIN_COLORS.surface,
  border: `1px solid ${ADMIN_COLORS.border}`,
  borderRadius: "24px",
  boxShadow: ADMIN_COLORS.shadow,
};

export const primaryButtonSx = {
  borderRadius: "14px",
  textTransform: "none",
  fontWeight: 850,
  fontFamily: FONT_FAMILY,
  bgcolor: ADMIN_COLORS.primary,
  boxShadow: "0 14px 30px rgba(30,75,129,.22)",
  "&:hover": { bgcolor: ADMIN_COLORS.primaryDark, boxShadow: "0 18px 36px rgba(30,75,129,.28)" },
};

export const secondaryButtonSx = {
  borderRadius: "14px",
  textTransform: "none",
  fontWeight: 800,
  fontFamily: FONT_FAMILY,
  color: ADMIN_COLORS.primary,
  borderColor: "rgba(30,75,129,.24)",
  bgcolor: "#fff",
  "&:hover": { borderColor: ADMIN_COLORS.primary, bgcolor: ADMIN_COLORS.primarySoft },
};

export const dangerButtonSx = {
  borderRadius: "14px",
  textTransform: "none",
  fontWeight: 800,
  fontFamily: FONT_FAMILY,
  color: ADMIN_COLORS.danger,
  borderColor: "rgba(211,47,47,.24)",
  "&:hover": { borderColor: ADMIN_COLORS.danger, bgcolor: ADMIN_COLORS.dangerSoft },
};

export function AdminPage({ children }) {
  useAdminGuard();
  return <Box sx={adminPageSx}>{children}</Box>;
}

export function AdminHeader({ eyebrow = "Administración", title, subtitle, icon, actions, backTo }) {
  const navigate = useNavigate();
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.8, md: 2.4 }, mb: 2.2, overflow: "hidden" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1.6}>
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ minWidth: 0 }}>
          {backTo ? (
            <Tooltip title="Volver">
              <IconButton onClick={() => navigate(backTo)} sx={{ bgcolor: ADMIN_COLORS.primarySoft, color: ADMIN_COLORS.primary }}>
                <ArrowBackRounded />
              </IconButton>
            </Tooltip>
          ) : null}
          <Avatar sx={{ width: 48, height: 48, bgcolor: ADMIN_COLORS.primarySoft, color: ADMIN_COLORS.primary, border: `1px solid ${ADMIN_COLORS.primary}22` }}>
            {icon || <ShieldRounded />}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Chip
              label={eyebrow}
              size="small"
              sx={{ height: 24, mb: .7, bgcolor: ADMIN_COLORS.primarySoft, color: ADMIN_COLORS.primary, border: `1px solid ${ADMIN_COLORS.primary}22`, fontWeight: 850, fontFamily: FONT_FAMILY }}
            />
            <Typography sx={{ color: ADMIN_COLORS.textStrong, fontWeight: 950, fontSize: { xs: "1.45rem", md: "1.95rem" }, letterSpacing: "-.055em", lineHeight: 1.05, fontFamily: FONT_FAMILY }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography sx={{ color: ADMIN_COLORS.muted, fontWeight: 520, fontSize: { xs: ".82rem", md: ".9rem" }, lineHeight: 1.55, mt: .6, maxWidth: 850, fontFamily: FONT_FAMILY }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        {actions ? <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>{actions}</Stack> : null}
      </Stack>
    </Paper>
  );
}

export function StatCard({ title, value, subtitle, icon, color = ADMIN_COLORS.primary, bg = ADMIN_COLORS.primarySoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 1.7, height: "100%", transition: "all .18s ease", "&:hover": { transform: "translateY(-2px)", borderColor: `${color}44` } }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Avatar sx={{ width: 46, height: 46, bgcolor: bg, color, border: `1px solid ${color}22` }}>{icon}</Avatar>
        <Box>
          <Typography sx={{ color, fontWeight: 950, fontSize: "1.55rem", lineHeight: 1, letterSpacing: "-.05em", fontFamily: FONT_FAMILY }}>{value}</Typography>
          <Typography sx={{ color: ADMIN_COLORS.textStrong, fontWeight: 850, fontSize: ".78rem", fontFamily: FONT_FAMILY }}>{title}</Typography>
          {subtitle ? <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: ".68rem", fontFamily: FONT_FAMILY }}>{subtitle}</Typography> : null}
        </Box>
      </Stack>
    </Paper>
  );
}

export function RoleChip({ role, size = "small" }) {
  const meta = getRoleMeta(role);
  return (
    <Chip
      size={size}
      icon={React.cloneElement(meta.icon, { sx: { fontSize: size === "small" ? 15 : 18 } })}
      label={meta.label}
      sx={{ bgcolor: meta.bg, color: meta.color, border: `1px solid ${meta.color}22`, fontWeight: 850, fontFamily: FONT_FAMILY }}
    />
  );
}

export function ProcessChip({ type, size = "small" }) {
  const meta = getProcessMeta(type);
  return <Chip size={size} label={meta.label} sx={{ bgcolor: meta.bg, color: meta.color, border: `1px solid ${meta.color}22`, fontWeight: 850, fontFamily: FONT_FAMILY }} />;
}

export function SearchField({ value, onChange, placeholder = "Buscar...", sx }) {
  return (
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      size="small"
      sx={{
        minWidth: { xs: "100%", md: 330 },
        "& .MuiOutlinedInput-root": { height: 42, borderRadius: "14px", bgcolor: "#fff", fontFamily: FONT_FAMILY, "& fieldset": { borderColor: ADMIN_COLORS.border }, "&:hover fieldset": { borderColor: ADMIN_COLORS.primary }, "&.Mui-focused fieldset": { borderColor: ADMIN_COLORS.primary } },
        ...sx,
      }}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded sx={{ color: ADMIN_COLORS.muted, fontSize: 19 }} /></InputAdornment> }}
    />
  );
}

export function LoadingState({ text = "Cargando información..." }) {
  return (
    <Box sx={{ minHeight: "72vh", display: "grid", placeItems: "center", bgcolor: ADMIN_COLORS.page }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 4, textAlign: "center" }}>
        <CircularProgress sx={{ color: ADMIN_COLORS.primary, mb: 2 }} />
        <Typography sx={{ color: ADMIN_COLORS.textStrong, fontWeight: 900, fontFamily: FONT_FAMILY }}>{text}</Typography>
      </Paper>
    </Box>
  );
}

export function EmptyState({ title = "Sin resultados", text = "No hay elementos para mostrar.", icon }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 4, textAlign: "center" }}>
      <Avatar sx={{ width: 54, height: 54, mx: "auto", mb: 1.4, bgcolor: ADMIN_COLORS.primarySoft, color: ADMIN_COLORS.primary }}>{icon || <SearchRounded />}</Avatar>
      <Typography sx={{ color: ADMIN_COLORS.textStrong, fontWeight: 950, fontSize: "1rem", fontFamily: FONT_FAMILY }}>{title}</Typography>
      <Typography sx={{ color: ADMIN_COLORS.muted, mt: .5, fontFamily: FONT_FAMILY }}>{text}</Typography>
    </Paper>
  );
}

export function ErrorAlert({ error }) {
  if (!error) return null;
  return <Alert severity="error" sx={{ mb: 2, borderRadius: "16px" }}>{error}</Alert>;
}

export function SectionCard({ title, subtitle, action, children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden", ...sx }}>
      {(title || action) && (
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1} sx={{ p: 1.8, borderBottom: `1px solid ${ADMIN_COLORS.border}`, bgcolor: ADMIN_COLORS.surfaceSoft }}>
          <Box>
            {title ? <Typography sx={{ color: ADMIN_COLORS.textStrong, fontWeight: 950, fontSize: "1rem", fontFamily: FONT_FAMILY }}>{title}</Typography> : null}
            {subtitle ? <Typography sx={{ color: ADMIN_COLORS.muted, fontSize: ".75rem", mt: .25, fontFamily: FONT_FAMILY }}>{subtitle}</Typography> : null}
          </Box>
          {action}
        </Stack>
      )}
      {children}
    </Paper>
  );
}

export const tableHeaderSx = {
  bgcolor: ADMIN_COLORS.surfaceSoft,
  "& th": {
    color: ADMIN_COLORS.muted,
    fontWeight: 900,
    fontSize: ".72rem",
    textTransform: "uppercase",
    letterSpacing: ".04em",
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${ADMIN_COLORS.border}`,
  },
};

export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "#fff",
    fontFamily: FONT_FAMILY,
  },
  "& .MuiInputLabel-root": { fontFamily: FONT_FAMILY },
};

export function copyToClipboard(text, onOk, onError) {
  navigator.clipboard?.writeText(text).then(onOk).catch(onError);
}
